import { Box, SxProps } from '@mui/material';
import React, {
  ReactNode,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import C from './ScrollableContainer.css';

export const ScrollableContainer = ({
  elements,
  sx,
  gap,
}: {
  elements: JSX.Element | JSX.Element[] | ReactNode[];
  sx?: SxProps;
  gap?: string;
}) => {
  const [containerOverflowing, setContainerOverflowing] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const containerRef = useRef<HTMLElement>(null);

  const checkOverflowAndEnds = useCallback(() => {
    const element = containerRef.current;
    if (!element) return;

    const isOverflowing = element.scrollWidth > element.clientWidth;
    const isAtStart = element.scrollLeft <= 1;
    const isAtEnd =
      Math.abs(
        element.scrollWidth - element.scrollLeft - element.clientWidth,
      ) <= 1;

    setContainerOverflowing(isOverflowing);
    setAtStart(isAtStart);
    setAtEnd(isAtEnd);
  }, [elements]);

  useEffect(() => {
    const checkWithDelay = () => {
      setTimeout(checkOverflowAndEnds, 0);
    };

    checkWithDelay();
    window.addEventListener('resize', checkOverflowAndEnds);

    const observer = new MutationObserver(checkWithDelay);
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      window.removeEventListener('resize', checkOverflowAndEnds);
      observer.disconnect();
    };
  }, [checkOverflowAndEnds]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', checkOverflowAndEnds);
      return () => {
        container.removeEventListener('scroll', checkOverflowAndEnds);
      };
    }
  }, [checkOverflowAndEnds]);

  const handleScroll = (direction: 'left' | 'right') => {
    if (containerRef.current) {
      const scrollAmount = containerRef.current.clientWidth * 0.8;
      containerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        padding: '0.2rem',
        alignItems: 'center',
        minWidth: '0',
        ...sx,
      }}
    >
      {containerOverflowing && !atStart && (
        <ChevronLeftIcon
          sx={{
            color: 'var(--color-subtext)',
            position: 'absolute',
            left: 0,
            width: '2rem',
            height: '100%',
            zIndex: 5,
            background: 'white',
            cursor: 'pointer',
            borderRadius: '1rem',
          }}
          onClick={() => handleScroll('left')}
          className={C.scrollBtn}
        />
      )}
      <Box
        ref={containerRef}
        sx={{
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          whiteSpace: 'nowrap',
          display: 'flex',
          width: '100%',
          gap: gap || '0.5rem',
        }}
      >
        {elements}
      </Box>
      {containerOverflowing && !atEnd && (
        <ChevronRightIcon
          sx={{
            position: 'absolute',
            right: 0,
            color: 'var(--color-subtext)',
            paddingLeft: '0.5rem',
            width: '2rem',
            height: '100%',
            zIndex: 5,
            background: 'white',
            cursor: 'pointer',
            borderRadius: '1rem',
          }}
          onClick={() => handleScroll('right')}
          className={C.scrollBtn}
        />
      )}
    </Box>
  );
};
